import React, { useState, useEffect } from 'react';
import { DollarSign, Search, Printer, FileText } from 'lucide-react';
import IfadaOrderSearchPaymentModal from './modals/IfadaOrderSearchPaymentModal';
import IfadaDailyReportModal from './modals/IfadaDailyReportModal';
import IfadaReceiptModal from './modals/IfadaReceiptModal';
import ReceiptModal from './modals/ReceiptModal';
import PaymentModal from './modals/PaymentModal';
import DailyReportModal from './modals/DailyReportModal';
import ConfirmDialog from './modals/ConfirmDialog';

const Payments = ({ apiCall, selectedYear, showSuccess, showError, hasRole }) => {
  // Tab state
  const [activeTab, setActiveTab] = useState('usd'); // 'usd' or 'lbp'
  
  // USD Payments state
  const [usdPayments, setUsdPayments] = useState([]);
  const [families, setFamilies] = useState([]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [showUsdReport, setShowUsdReport] = useState(false);
  
  // LBP/Ifada Payments state
  const [lbpPayments, setLbpPayments] = useState([]);
  const [showIfadaPaymentModal, setShowIfadaPaymentModal] = useState(false);
  const [showIfadaDailyReport, setShowIfadaDailyReport] = useState(false);
  const [showIfadaReceipt, setShowIfadaReceipt] = useState(false);
  const [selectedPayment, setSelectedPayment] = useState(null);
  
  // USD Receipt state
  const [showUsdReceipt, setShowUsdReceipt] = useState(false);
  
  // Auto-show receipt after payment - IMPROVED
  const [pendingReceiptPaymentId, setPendingReceiptPaymentId] = useState(null);
  const [pendingUsdReceiptPaymentId, setPendingUsdReceiptPaymentId] = useState(null); // ✅ FIX: Added for USD auto-receipt

  // Void (GlobalAdmin only) — payment awaiting confirmation
  const [voidTarget, setVoidTarget] = useState(null);
  const [voiding, setVoiding] = useState(false);
  // Voided payments are hidden by default; admins can bring them back into
  // view to check what was reversed and why.
  const [showVoided, setShowVoided] = useState(false);
  
  // Filters - DEFAULT TO TODAY
  const [searchTerm, setSearchTerm] = useState('');
  const [dateFilter, setDateFilter] = useState('today');
  const today = new Date().toISOString().split('T')[0];
  const [startDate, setStartDate] = useState(today);
  const [endDate, setEndDate] = useState(today);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedYear, activeTab, showVoided]);

  // ✅ FIX: Auto-show receipt after payment loads
  useEffect(() => {
    if (pendingReceiptPaymentId && lbpPayments.length > 0) {
      console.log('Looking for payment ID:', pendingReceiptPaymentId);
      console.log('LBP Payments:', lbpPayments);
      
      const payment = lbpPayments.find(p => p.PaymentID === pendingReceiptPaymentId);
      
      if (payment) {
        console.log('✅ Payment found, showing receipt:', payment);
        setSelectedPayment(payment);
        setShowIfadaReceipt(true);
        setPendingReceiptPaymentId(null);
      } else {
        console.log('⚠️ Payment not found yet, will retry...');
      }
    }
  }, [pendingReceiptPaymentId, lbpPayments]);

  // ✅ FIX: Auto-show receipt after USD payment loads
  useEffect(() => {
    if (pendingUsdReceiptPaymentId && usdPayments.length > 0) {
      const payment = usdPayments.find(p => p.PaymentID === pendingUsdReceiptPaymentId);
      if (payment) {
        setSelectedPayment(payment);
        setShowUsdReceipt(true);
        setPendingUsdReceiptPaymentId(null);
      }
    }
  }, [pendingUsdReceiptPaymentId, usdPayments]);

  const loadData = async () => {
    try {
      setLoading(true);
      
      if (activeTab === 'usd') {
        // Load USD payments ONLY (exclude LBP/Ifada)
        const [paymentsData, familiesData] = await Promise.all([
          apiCall(`/payments?year=${selectedYear}${showVoided ? '&includeVoided=true' : ''}`, { method: 'GET' }),
          apiCall(`/families?academicYear=${selectedYear}`, { method: 'GET' })
        ]);
        
        // Filter OUT Ifada/LBP payments from USD tab
        const usdOnly = (paymentsData || []).filter(p => 
          p.Currency !== 'LBP' && p.Module !== 'Ifada'
        );
        setUsdPayments(usdOnly);
        setFamilies(familiesData || []);
      } else {
        // Load LBP/Ifada payments ONLY
        const paymentsData = await apiCall(`/payments?year=${selectedYear}${showVoided ? '&includeVoided=true' : ''}`, {
          method: 'GET'
        });
        
        // Filter ONLY Ifada/LBP payments for LBP tab
        const lbpOnly = (paymentsData || []).filter(p => 
          p.Currency === 'LBP' && p.Module === 'Ifada'
        );
        
        console.log('Loaded LBP payments:', lbpOnly);
        setLbpPayments(lbpOnly);
        
        // Also load families for report
        const familiesData = await apiCall(`/families?academicYear=${selectedYear}`, { 
          method: 'GET' 
        });
        setFamilies(familiesData || []);
      }
    } catch (error) {
      console.error('Error loading data:', error);
      showError('Error loading payments: ' + error.message);
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (amount, currency = 'USD') => {
    if (currency === 'LBP') {
      return new Intl.NumberFormat('en-US').format(amount) + ' ل.ل';
    }
    return '$' + new Intl.NumberFormat('en-US', { minimumFractionDigits: 2 }).format(amount);
  };

  const getFilteredPayments = () => {
    const payments = activeTab === 'usd' ? usdPayments : lbpPayments;
    let filtered = [...payments];

    // Search filter
    if (searchTerm) {
      const search = searchTerm.toLowerCase();
      filtered = filtered.filter(p => 
        p.FamilyID?.toLowerCase().includes(search) ||
        p.PaymentMethod?.toLowerCase().includes(search) ||
        p.PaymentID?.toString().includes(search) ||
        p.ReferenceNumber?.toLowerCase().includes(search)
      );
    }

    // Date filter
    if (dateFilter === 'today') {
      const today = new Date().toDateString();
      filtered = filtered.filter(p => 
        new Date(p.PaymentDate).toDateString() === today
      );
    } else if (dateFilter === 'week') {
      const weekAgo = new Date();
      weekAgo.setDate(weekAgo.getDate() - 7);
      filtered = filtered.filter(p => 
        new Date(p.PaymentDate) >= weekAgo
      );
    } else if (dateFilter === 'month') {
      const monthAgo = new Date();
      monthAgo.setMonth(monthAgo.getMonth() - 1);
      filtered = filtered.filter(p => 
        new Date(p.PaymentDate) >= monthAgo
      );
    } else if (dateFilter === 'custom' && startDate && endDate) {
      filtered = filtered.filter(p => {
        const paymentDate = new Date(p.PaymentDate);
        return paymentDate >= new Date(startDate) && paymentDate <= new Date(endDate);
      });
    }

    return filtered;
  };

  const isVoided = (p) => p && p.Status === 'Voided';

  // Voided money is reversed — it must never be added to a displayed total,
  // even while the rows are on screen for review.
  const calculateTotal = () => {
    return getFilteredPayments()
      .filter((p) => !isVoided(p))
      .reduce((sum, p) => sum + (p.Amount || 0), 0);
  };

  const voidedCount = () => getFilteredPayments().filter(isVoided).length;

  // ✅ FIX: Handle print receipt for Ifada
  // Void reverses the payment everywhere it was applied and recomputes the
  // family balance. The row is kept (marked Voided) so a receipt already handed
  // to a family can still be traced.
  const handleVoidPayment = async (reason) => {
    if (!voidTarget) return;
    try {
      setVoiding(true);
      const result = await apiCall(`/payments/${voidTarget.PaymentID}/void`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ reason }),
      });
      showSuccess(result.message || `Payment #${voidTarget.PaymentID} voided`);
      setVoidTarget(null);
      loadData();
    } catch (error) {
      showError('Error voiding payment: ' + error.message);
    } finally {
      setVoiding(false);
    }
  };

  const handlePrintReceipt = (payment) => {
    console.log('🖨️ Print Receipt clicked for payment:', payment);
    setSelectedPayment(payment);
    setShowIfadaReceipt(true);
  };

  // ✅ NEW: Handle view details for USD
  const handleViewUsdDetails = (payment) => {
    console.log('👁️ View Details clicked for payment:', payment);
    setSelectedPayment(payment);
    setShowUsdReceipt(true);
  };

  const handleIfadaDailyReport = () => {
    setShowIfadaDailyReport(true);
  };

  const handleUsdDailyReport = () => {
    setShowUsdReport(true);
  };

  // ✅ FIX: Handle payment success - trigger receipt
  const handleIfadaPaymentSuccess = async (paymentId) => {
    console.log('🎉 Payment successful, ID:', paymentId);
    
    // Set pending receipt
    setPendingReceiptPaymentId(paymentId);
    
    // Reload data to get the new payment
    await loadData();
  };

  // ✅ FIX: Handle USD payment success - close modal, reload, show receipt
  const handleUsdPaymentSuccess = async (paymentId) => {
    console.log('🎉 USD Payment successful, ID:', paymentId);
    setShowPaymentModal(false);
    showSuccess('Payment recorded successfully!');
    setPendingUsdReceiptPaymentId(paymentId);
    await loadData();
  };

  return (
    <div className="space-y-6">
      {/* Tab Navigation */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200">
        <div className="flex border-b">
          <button
            onClick={() => setActiveTab('usd')}
            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
              activeTab === 'usd'
                ? 'border-b-2 border-blue-600 text-blue-600 bg-blue-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <DollarSign size={20} />
              <span>USD Payments ($)</span>
            </div>
            <p className="text-xs mt-1 text-gray-500">Afterschool, Bus, etc.</p>
          </button>
          
          <button
            onClick={() => setActiveTab('lbp')}
            className={`flex-1 px-6 py-4 font-semibold transition-colors ${
              activeTab === 'lbp'
                ? 'border-b-2 border-purple-600 text-purple-600 bg-purple-50'
                : 'text-gray-600 hover:text-gray-800 hover:bg-gray-50'
            }`}
          >
            <div className="flex items-center justify-center gap-2">
              <FileText size={20} />
              <span>LBP Payments (ل.ل)</span>
            </div>
            <p className="text-xs mt-1 text-gray-500">Ifada / إفادات</p>
          </button>
        </div>

        {/* Header with Actions */}
        <div className="p-6 border-b bg-gray-50">
          <div className="flex justify-between items-center mb-4">
            <div>
              <h2 className="text-xl font-bold text-gray-800">
                {activeTab === 'usd' ? 'USD Payments' : 'Ifada Payments (Lebanese Pounds)'}
              </h2>
              <p className="text-sm text-gray-600 mt-1">
                {activeTab === 'usd' 
                  ? 'Manage payments for Afterschool, Bus, and other USD modules'
                  : 'Manage payments for Ifada orders (إفادات ومستندات)'
                }
              </p>
            </div>
            
            <div className="flex gap-2">
              {/* Daily Report button for BOTH tabs */}
              <button
                onClick={activeTab === 'usd' ? handleUsdDailyReport : handleIfadaDailyReport}
                className={`px-4 py-2 ${
                  activeTab === 'usd' ? 'bg-blue-600 hover:bg-blue-700' : 'bg-purple-600 hover:bg-purple-700'
                } text-white rounded-lg flex items-center gap-2 transition-colors`}
              >
                <Printer size={18} />
                Daily Report
              </button>
              
              {/* Record Payment button for BOTH tabs */}
              <button
                onClick={() => activeTab === 'usd' ? setShowPaymentModal(true) : setShowIfadaPaymentModal(true)}
                className={`px-6 py-2 ${
                  activeTab === 'usd' 
                    ? 'bg-blue-600 hover:bg-blue-700' 
                    : 'bg-green-600 hover:bg-green-700'
                } text-white rounded-lg flex items-center gap-2 font-semibold transition-colors shadow-lg`}
              >
                <DollarSign size={20} />
                Record Payment {activeTab === 'lbp' && '(ل.ل)'}
              </button>
            </div>
          </div>

          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Search */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={18} />
              <input
                type="text"
                placeholder="Search payments..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>

            {/* Date Filter */}
            <select
              value={dateFilter}
              onChange={(e) => setDateFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="today">Today</option>
              <option value="week">Last 7 Days</option>
              <option value="month">Last 30 Days</option>
              <option value="all">All Time</option>
              <option value="custom">Custom Range</option>
            </select>

            {/* Custom Date Range */}
            {dateFilter === 'custom' && (
              <>
                <input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
                <input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                />
              </>
            )}

            {/* Admins can pull voided payments back into view to see what was
                reversed and why. They never count toward the totals. */}
            {hasRole && hasRole('GlobalAdmin') && (
              <label className="flex items-center gap-2 px-3 py-2 text-sm text-gray-700 cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={showVoided}
                  onChange={(e) => setShowVoided(e.target.checked)}
                  className="w-4 h-4 text-red-600 rounded border-gray-300"
                />
                Show voided
              </label>
            )}
          </div>
        </div>

        {/* Summary Stats */}
        <div className="p-6 bg-gradient-to-r from-blue-50 to-purple-50 border-b">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Total Payments</p>
              <p className="text-2xl font-bold text-gray-800">
                {getFilteredPayments().length - voidedCount()}
              </p>
              {voidedCount() > 0 && (
                <p className="text-xs text-red-600 mt-0.5">
                  + {voidedCount()} voided (not counted)
                </p>
              )}
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Total Amount</p>
              <p className={`text-2xl font-bold ${activeTab === 'usd' ? 'text-blue-600' : 'text-purple-600'}`}>
                {formatCurrency(calculateTotal(), activeTab === 'usd' ? 'USD' : 'LBP')}
              </p>
            </div>
            <div className="bg-white rounded-lg p-4 shadow-sm">
              <p className="text-sm text-gray-600 mb-1">Date Range</p>
              <p className="text-sm font-semibold text-gray-800">
                {dateFilter === 'all' && 'All Time'}
                {dateFilter === 'today' && 'Today'}
                {dateFilter === 'week' && 'Last 7 Days'}
                {dateFilter === 'month' && 'Last 30 Days'}
                {dateFilter === 'custom' && startDate && endDate && 
                  `${new Date(startDate).toLocaleDateString()} - ${new Date(endDate).toLocaleDateString()}`
                }
              </p>
            </div>
          </div>
        </div>

        {/* Payments Table */}
        <div className="p-6">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
              <p className="text-gray-600 mt-4">Loading payments...</p>
            </div>
          ) : getFilteredPayments().length === 0 ? (
            <div className="text-center py-12">
              <FileText size={64} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-600 text-lg">No payments found</p>
              <p className="text-gray-500 text-sm mt-2">
                {searchTerm || dateFilter !== 'all' 
                  ? 'Try adjusting your filters' 
                  : 'No payments recorded yet'
                }
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b-2 border-gray-200 bg-gray-50">
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Receipt #</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Date</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Family</th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">
                      {activeTab === 'usd' ? 'Module' : 'Reference'}
                    </th>
                    <th className="px-4 py-3 text-left text-sm font-semibold text-gray-700">Method</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold text-gray-700">Amount</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold text-gray-700">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {getFilteredPayments().map((payment) => (
                    <tr
                      key={payment.PaymentID}
                      className={`border-b border-gray-100 transition-colors ${
                        isVoided(payment) ? 'bg-red-50/60 text-gray-400' : 'hover:bg-gray-50'
                      }`}
                    >
                      <td className="px-4 py-3">
                        <span className={`font-semibold ${isVoided(payment) ? 'text-gray-400 line-through' : 'text-blue-600'}`}>
                          #{payment.PaymentID}
                        </span>
                        {isVoided(payment) && (
                          <div
                            className="mt-0.5 inline-block px-1.5 py-0.5 bg-red-100 text-red-700 text-[10px] font-semibold rounded"
                            title={`Voided${payment.VoidedByName ? ' by ' + payment.VoidedByName : ''}`
                              + (payment.VoidedAt ? ' on ' + new Date(payment.VoidedAt).toLocaleString() : '')
                              + (payment.VoidReason ? ' — ' + payment.VoidReason : '')}
                          >
                            VOIDED
                          </div>
                        )}
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {new Date(payment.PaymentDate).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="text-sm">
                          <div className="font-medium text-gray-800">{payment.FamilyID}</div>
                          {payment.Notes && (
                            <div className="text-xs text-gray-500 truncate max-w-xs">
                              {payment.Notes}
                            </div>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          payment.Module === 'Ifada' 
                            ? 'bg-purple-100 text-purple-700'
                            : payment.Module === 'Afterschool'
                            ? 'bg-blue-100 text-blue-700'
                            : payment.Module === 'Activities'
                            ? 'bg-orange-100 text-orange-700'
                            : payment.Module === 'Daycare'
                            ? 'bg-pink-100 text-pink-700'
                            : payment.Module === 'ArtSchool'
                            ? 'bg-indigo-100 text-indigo-700'
                            : 'bg-green-100 text-green-700'
                        }`}>
                          {payment.ReferenceNumber || payment.Module}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-sm text-gray-700">
                        {payment.PaymentMethod}
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={`font-bold ${
                          activeTab === 'usd' ? 'text-blue-600' : 'text-purple-600'
                        }`}>
                          {formatCurrency(payment.Amount, payment.Currency || (activeTab === 'usd' ? 'USD' : 'LBP'))}
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex items-center justify-center gap-3">
                          {activeTab === 'lbp' ? (
                            <button
                              onClick={() => handlePrintReceipt(payment)}
                              className="text-purple-600 hover:text-purple-800 text-sm font-medium hover:underline"
                            >
                              Print Receipt
                            </button>
                          ) : (
                            <button
                              onClick={() => handleViewUsdDetails(payment)}
                              className="text-blue-600 hover:text-blue-800 text-sm font-medium hover:underline"
                            >
                              View Receipt
                            </button>
                          )}
                          {/* Admin-only. Voiding reverses the money everywhere
                              but keeps the row, so a printed receipt can still
                              be traced back. */}
                          {hasRole && hasRole('GlobalAdmin') && !isVoided(payment) && (
                            <button
                              onClick={() => setVoidTarget(payment)}
                              className="text-red-600 hover:text-red-800 text-sm font-medium hover:underline"
                              title="Void this payment and reverse it from the family's balance"
                            >
                              Void
                            </button>
                          )}
                          {isVoided(payment) && (
                            <span className="text-xs text-gray-400 italic">
                              {payment.VoidReason || 'voided'}
                            </span>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
                <tfoot>
                  <tr className={`border-t-2 ${
                    activeTab === 'usd' ? 'bg-blue-50 border-blue-200' : 'bg-purple-50 border-purple-200'
                  }`}>
                    <td colSpan="5" className="px-4 py-3 text-right font-bold text-gray-700">
                      Total:
                    </td>
                    <td className="px-4 py-3 text-right">
                      <span className={`text-xl font-bold ${
                        activeTab === 'usd' ? 'text-blue-600' : 'text-purple-600'
                      }`}>
                        {formatCurrency(calculateTotal(), activeTab === 'usd' ? 'USD' : 'LBP')}
                      </span>
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}
        </div>
      </div>

      {/* ✅ FIX: USD Payment Modal - added onSuccess prop */}
      {showPaymentModal && (
        <PaymentModal
          onClose={() => {
            setShowPaymentModal(false);
            loadData();
          }}
          onSuccess={handleUsdPaymentSuccess}
          apiCall={apiCall}
          families={families}
          selectedYear={selectedYear}
          showError={showError}
        />
      )}

      {/* USD Daily Report Modal */}
      {showUsdReport && (
        <DailyReportModal
          payments={getFilteredPayments()}
          families={families}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setShowUsdReport(false)}
          apiCall={apiCall}
        />
      )}

      {/* USD Receipt Modal */}
      {showUsdReceipt && selectedPayment && (
        <ReceiptModal
          payment={selectedPayment}
          apiCall={apiCall}
          onClose={() => {
            setShowUsdReceipt(false);
            setSelectedPayment(null);
          }}
        />
      )}

      {/* Ifada Payment Modal */}
      {showIfadaPaymentModal && (
        <IfadaOrderSearchPaymentModal
          onClose={() => {
            setShowIfadaPaymentModal(false);
          }}
          onPaymentSuccess={handleIfadaPaymentSuccess}
          apiCall={apiCall}
          selectedYear={selectedYear}
          showError={showError}
          showSuccess={showSuccess}
        />
      )}

      {/* Ifada Daily Report Modal */}
      {showIfadaDailyReport && (
        <IfadaDailyReportModal
          payments={getFilteredPayments()}
          families={families}
          startDate={startDate}
          endDate={endDate}
          onClose={() => setShowIfadaDailyReport(false)}
        />
      )}

      {/* Ifada Receipt Modal */}
      {showIfadaReceipt && selectedPayment && (
        <IfadaReceiptModal
          payment={selectedPayment}
          apiCall={apiCall}
          onClose={() => {
            setShowIfadaReceipt(false);
            setSelectedPayment(null);
          }}
        />
      )}

      {/* Void confirmation (GlobalAdmin only) */}
      <ConfirmDialog
        open={!!voidTarget}
        tone="danger"
        title={`Void payment #${voidTarget?.PaymentID ?? ''}`}
        message={voidTarget
          ? `Reverse ${formatCurrency(voidTarget.Amount, voidTarget.Currency || 'USD')} paid by family ${voidTarget.FamilyID} for ${voidTarget.Module}?`
          : ''}
        details={
          'The amount comes back onto the family’s balance and any charges it settled reopen. '
          + 'The payment is kept and marked Voided — it stays traceable against the printed receipt — '
          + 'but it stops counting in reports and totals. This cannot be undone from the UI.'
        }
        confirmLabel="Void payment"
        cancelLabel="Keep it"
        requireInput
        inputLabel="Reason for voiding"
        inputPlaceholder="e.g. entered twice, wrong family, cheque bounced"
        busy={voiding}
        onConfirm={handleVoidPayment}
        onCancel={() => setVoidTarget(null)}
      />
    </div>
  );
};

export default Payments;